import { useState, useEffect } from 'react'
import { usePioneerStore, ChartData, ZODIAC_GLYPHS, ZODIAC_ELEMENTS } from '../store/pioneerStore'
import {
  ScreenWrapper,
  EngravingDivider,
  GoldButton,
  GoldInput,
  ProgressDots,
  EightStar,
} from '../components/shared'

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormErrors {
  name?: string
  date?: string
  time?: string
  city?: string
}

// Props accepted from GP1 App.tsx (analytics bridge — logged but not required)
interface Screen2Props {
  onAnalyticsEvent?: (event: string, props?: Record<string, unknown>) => void
}

// ─── Timezone detection from city (stub → replace with geocoding API) ─────────
const CITY_TIMEZONE_MAP: Record<string, { tz: string; lat: number; lng: number }> = {
  bangkok:       { tz: 'Asia/Bangkok',       lat: 13.7563,  lng: 100.5018  },
  'surat thani': { tz: 'Asia/Bangkok',       lat: 9.1382,   lng: 99.3214   },
  london:        { tz: 'Europe/London',      lat: 51.5074,  lng: -0.1278   },
  'new york':    { tz: 'America/New_York',   lat: 40.7128,  lng: -74.006   },
  'los angeles': { tz: 'America/Los_Angeles',lat: 34.0522,  lng: -118.2437 },
  paris:         { tz: 'Europe/Paris',       lat: 48.8566,  lng: 2.3522    },
  tokyo:         { tz: 'Asia/Tokyo',         lat: 35.6762,  lng: 139.6503  },
  singapore:     { tz: 'Asia/Singapore',     lat: 1.3521,   lng: 103.8198  },
  sydney:        { tz: 'Australia/Sydney',   lat: -33.8688, lng: 151.2093  },
  dubai:         { tz: 'Asia/Dubai',         lat: 25.2048,  lng: 55.2708   },
  'kuala lumpur':{ tz: 'Asia/Kuala_Lumpur',  lat: 3.139,    lng: 101.6869  },
}

function detectTimezone(city: string): { tz: string; lat: number; lng: number } | null {
  const key = city.toLowerCase().trim()
  return CITY_TIMEZONE_MAP[key] ?? null
}

// ─── Mock chart calculation ───────────────────────────────────────────────────
// REPLACE with actual Swiss Ephemeris API call:
// POST /api/calculate-chart { date, time, lat, lng, tz }
async function mockCalculateChart(
  _date: string,
  _time: string,
  lat: number,
  lng: number,
): Promise<ChartData> {
  await new Promise((r) => setTimeout(r, 1800))

  return {
    sun: {
      sign: 'Aries',
      degree: 3.7,
      house: 1,
      glyph: ZODIAC_GLYPHS['Aries'],
      element: ZODIAC_ELEMENTS['Aries'],
      modality: 'Cardinal',
      description:
        "Your Sun in Aries places pure vital force at the core of your identity. You don't wait for permission — you arrive and the room shifts.",
    },
    moon: {
      sign: 'Leo',
      degree: 23.97,
      house: 5,
      glyph: ZODIAC_GLYPHS['Leo'],
      element: ZODIAC_ELEMENTS['Leo'],
      modality: 'Fixed',
      description:
        'Moon in Leo gives you emotional grandeur. You need to be witnessed in your fullness.',
    },
    rising: lat && lng
      ? {
          sign: 'Scorpio',
          degree: 13.47,
          glyph: ZODIAC_GLYPHS['Scorpio'],
          element: ZODIAC_ELEMENTS['Scorpio'],
          modality: 'Fixed',
          description:
            'Scorpio Rising means your presence lands before your words do.',
        }
      : null,
    placements: [],
    houses: [],
    aspects: [],
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function Screen2BirthData({ onAnalyticsEvent }: Screen2Props) {
  const { birthData, setBirthData, setChartData, goNext, goBack } = usePioneerStore()

  // Local state — keeps loading/unknownTime out of the global store
  const [isLoading, setIsLoading] = useState(false)
  const [unknownTime, setUnknownTime] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [tzDetected, setTzDetected] = useState<string | null>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60)
    return () => clearTimeout(t)
  }, [])

  // Auto-detect timezone when birthPlace changes
  const currentCity = birthData?.birthPlace ?? ''
  useEffect(() => {
    if (currentCity.length > 2) {
      const result = detectTimezone(currentCity)
      if (result) {
        setBirthData({ timezone: result.tz, lat: result.lat, lng: result.lng })
        setTzDetected(result.tz)
      } else {
        setTzDetected(null)
      }
    }
  }, [currentCity])

  function validate(): boolean {
    const e: FormErrors = {}
    if (!birthData?.name?.trim()) e.name = 'Your name is required'
    if (!birthData?.birthDate)    e.date = 'Birth date is required'
    if (!unknownTime && !birthData?.birthTime) e.time = "Enter your birth time or check \"I don't know\""
    if (!birthData?.birthPlace?.trim()) e.city = 'Birth city is required'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function handleContinue() {
    if (!validate()) return

    setIsLoading(true)
    onAnalyticsEvent?.('birth_data_continue_clicked')
    try {
      const geo = detectTimezone(birthData?.birthPlace ?? '')
      const lat = geo?.lat ?? birthData?.lat ?? 0
      const lng = geo?.lng ?? birthData?.lng ?? 0
      const chart = await mockCalculateChart(
        birthData?.birthDate ?? '',
        unknownTime ? '' : (birthData?.birthTime ?? ''),
        lat,
        lng
      )
      setChartData(chart)
      goNext()
    } catch {
      setErrors({ name: 'Calculation failed — please try again' })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <ScreenWrapper>
      <div
        className="flex flex-col items-center justify-center min-h-screen px-6 py-16"
        style={{
          maxWidth: 560,
          margin: '0 auto',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.7s ease, transform 0.7s ease',
        }}
      >
        {/* Progress */}
        <ProgressDots current={2} />

        {/* Header */}
        <div className="text-center mt-8 mb-2">
          <p
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: '0.6rem',
              letterSpacing: '0.22em',
              color: '#C9A34A88',
              textTransform: 'uppercase',
              marginBottom: '0.75rem',
            }}
          >
            Step 2 of 5
          </p>
          <h2
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(1.8rem, 5vw, 2.8rem)',
              fontWeight: 700,
              color: '#F5E6C8',
              lineHeight: 1.15,
            }}
          >
            Your Moment of
            <br />
            <em>First Light</em>
          </h2>
          <p
            style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.9rem',
              color: '#F5E6C877',
              marginTop: '0.75rem',
              lineHeight: 1.6,
            }}
          >
            The stars were in precise positions when you arrived.
            <br />
            We need that exact moment to read them.
          </p>
        </div>

        <EngravingDivider className="w-full my-6" />

        {/* Form */}
        <div className="w-full flex flex-col gap-5">
          {/* Name */}
          <GoldInput
            id="birth-name"
            label="Your full name"
            placeholder="As it appears on your birth certificate"
            value={birthData?.name ?? ''}
            onChange={(e) => setBirthData({ name: e.target.value })}
            error={errors.name}
            autoComplete="name"
          />

          {/* Date */}
          <GoldInput
            id="birth-date"
            label="Date of birth"
            type="date"
            value={birthData?.birthDate ?? ''}
            onChange={(e) => setBirthData({ birthDate: e.target.value })}
            error={errors.date}
            max={new Date().toISOString().split('T')[0]}
          />

          {/* Time */}
          <div className="flex flex-col gap-2">
            <GoldInput
              id="birth-time"
              label="Birth time (24-hour)"
              type="time"
              value={birthData?.birthTime ?? ''}
              onChange={(e) => {
                setBirthData({ birthTime: e.target.value })
                setUnknownTime(false)
              }}
              error={errors.time}
              disabled={unknownTime}
              placeholder="HH:MM"
            />
            <label
              className="flex items-center gap-2 cursor-pointer"
              style={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: '#F5E6C866' }}
            >
              <input
                type="checkbox"
                checked={unknownTime}
                onChange={(e) => {
                  setUnknownTime(e.target.checked)
                  if (e.target.checked) setBirthData({ birthTime: '' })
                }}
                style={{ accentColor: '#C9A34A', width: 14, height: 14 }}
              />
              I don't know my birth time{' '}
              <span style={{ color: '#C9A34A55', fontSize: '0.75rem' }}>
                (Rising sign won't be calculated)
              </span>
            </label>
          </div>

          {/* City → stored as birthPlace */}
          <div>
            <GoldInput
              id="birth-city"
              label="Birth city"
              placeholder="e.g. Bangkok, London, New York"
              value={birthData?.birthPlace ?? ''}
              onChange={(e) => setBirthData({ birthPlace: e.target.value })}
              error={errors.city}
              autoComplete="address-level2"
            />

            {/* Timezone indicator */}
            {tzDetected && (
              <div
                className="flex items-center gap-2 mt-2"
                style={{
                  fontFamily: '"DM Mono", monospace',
                  fontSize: '0.65rem',
                  letterSpacing: '0.1em',
                  color: '#7A9E5F',
                }}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                  <circle cx="5" cy="5" r="4" />
                </svg>
                Timezone detected: {tzDetected}
              </div>
            )}
            {(birthData?.birthPlace?.length ?? 0) > 2 && !tzDetected && (
              <p
                style={{
                  fontFamily: '"DM Mono", monospace',
                  fontSize: '0.63rem',
                  color: '#C9A34A66',
                  marginTop: '0.4rem',
                }}
              >
                Timezone not auto-detected — we'll verify during calculation.
              </p>
            )}
          </div>
        </div>

        {/* Privacy note */}
        <div
          className="flex items-start gap-2 mt-5 w-full"
          style={{
            background: '#C9A34A08',
            border: '1px solid #C9A34A22',
            padding: '12px 14px',
            borderRadius: 2,
          }}
        >
          <div style={{ marginTop: 3, flexShrink: 0 }}>
            <EightStar size={10} color="#C9A34A55" />
          </div>
          <p
            style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.75rem',
              color: '#F5E6C855',
              lineHeight: 1.5,
            }}
          >
            Your birth data is used only to calculate your chart and is never sold.
            You control what appears on your public profile.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between w-full mt-8 gap-4">
          <GoldButton
            variant="ghost"
            size="md"
            onClick={goBack}
            disabled={isLoading}
          >
            ← Back
          </GoldButton>
          <GoldButton
            size="md"
            onClick={handleContinue}
            loading={isLoading}
            style={{ flex: 1 }}
          >
            {isLoading ? 'Reading the stars…' : 'Calculate My Chart →'}
          </GoldButton>
        </div>

        {/* Loading state */}
        {isLoading && (
          <p
            className="mt-4 text-center"
            style={{
              fontFamily: '"Playfair Display", serif',
              fontStyle: 'italic',
              fontSize: '0.85rem',
              color: '#C9A34A88',
              animation: 'pulse 2s ease-in-out infinite',
            }}
          >
            Tracing your planetary positions across time and space…
          </p>
        )}
      </div>
    </ScreenWrapper>
  )
}
